import * as React from 'react'

import * as Sentry from '@sentry/browser'
import { EnvironmentContext } from 'config/environment'
import {
  DataTableProjection,
  DataTableSource,
  ProjectionKind,
} from 'generated/resources/datatable.graphql'
import once from 'lodash/once'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

interface Props<T, SourceFields extends string> {
  children: React.ReactNode
  source: DataTableSource<T, SourceFields>
}

interface Context<T, SourceFields extends string> {
  projections: DataTableProjection<T, SourceFields>[]
  setProjections: (projections: DataTableProjection<T, SourceFields>[]) => void
  initialProjections: DataTableProjection<T, SourceFields>[]
}

function getKey(tableIdentifier: string) {
  return `datatable-projections-${tableIdentifier}`
}

function notifyException(identifier: string, captureExplicitSentryErrors: boolean) {
  const errorMessage = `Could not find projection with identifier ${identifier}`

  // NOTE: This is here just to be aware if something does not work as expected. But be aware!
  // If a DataTable columns are removed, this will be triggered, so it's maybe not an error at
  // all. Remove it if it makes more noise than help.
  if (captureExplicitSentryErrors) {
    const error = new Error(errorMessage)
    Sentry.setTag('data_table', true)
    Sentry.setExtra('identifier', identifier)
    Sentry.captureException(error)
  } else {
    // eslint-disable-next-line no-console
    console.error(errorMessage)
  }
}

function getPersistedProjections<T, SourceFields extends string>(
  tableIdentifier: string,
  projections: DataTableProjection<T, SourceFields>[],
  captureExplicitSentryErrors: boolean
) {
  const key = getKey(tableIdentifier)
  const identifiersJSON = localStorage.getItem(key)
  const identifiers = identifiersJSON ? (JSON.parse(identifiersJSON) as string[]) : null

  const fixedProjections = projections.filter((projection) => projection.fixed)
  const decorativeProjections = projections.filter(
    (projection) => projection.kind === ProjectionKind.Decorative
  )
  const visibleProjections = projections.filter((projection) => {
    if ('hidden' in projection) {
      return !projection.hidden
    }
    return true
  })

  if (!identifiers) {
    return visibleProjections
  }

  let storageProjections: DataTableProjection<T, SourceFields>[] = []

  for (const identifier of identifiers) {
    const projection = projections.find((projection) => projection.identifier === identifier)

    if (!projection) {
      notifyException(identifier, captureExplicitSentryErrors)
      localStorage.removeItem(key)
      storageProjections = visibleProjections
      break
    }

    storageProjections.push(projection)
  }

  const fixedIdentifiers = fixedProjections.map((projection) => projection.identifier)
  const decorativeIdentifiers = decorativeProjections.map((projection) => projection.identifier)
  if (
    fixedIdentifiers.every((identifier) => identifiers.includes(identifier)) &&
    decorativeIdentifiers.every((identifier) => identifiers.includes(identifier))
  ) {
    return storageProjections
  }

  return visibleProjections
}

function saveProjections<T, SourceFields extends string>(
  tableIdentifier: string,
  projections: DataTableProjection<T, SourceFields>[]
) {
  const key = getKey(tableIdentifier)
  const identifiers = projections.map((projection) => projection.identifier)

  localStorage.setItem(key, JSON.stringify(identifiers))
}

export const createProjectionsContext = once(<T, SourceFields extends string>() =>
  React.createContext<Context<T, SourceFields> | null>(null)
)

export function ProjectionsProvider<T, SourceFields extends string>({
  children,
  source,
}: Props<T, SourceFields>) {
  const Context = createProjectionsContext<T, SourceFields>()
  const { captureExplicitSentryErrors } = React.useContext(EnvironmentContext)
  const [projections, setProjections] = React.useState(() =>
    getPersistedProjections<T, SourceFields>(
      source.identifier,
      source.projections,
      captureExplicitSentryErrors
    )
  )

  const setProjectionsWithLocalStorage = (projections: DataTableProjection<T, SourceFields>[]) => {
    setProjections(projections)
    saveProjections(source.identifier, projections)
  }

  const value = {
    projections,
    setProjections: setProjectionsWithLocalStorage,
    initialProjections: source.projections,
  }

  // NOTE: This is just here for development purposes, to see live changing projections if there's
  // some configuration change.
  useUnsafeEffect(() => {
    const newProjections = getPersistedProjections(
      source.identifier,
      source.projections,
      captureExplicitSentryErrors
    )
    setProjections(newProjections)
  }, [captureExplicitSentryErrors, source.projections, source.identifier])

  return <Context.Provider value={value}>{children}</Context.Provider>
}
